import { useQuery } from '@tanstack/react-query';

import {
  getPosition,
  getPositionLifecycle,
  getPositionPriceHistories,
  getPositionReplay,
  getTradingPortfolioHeat,
  listPositions,
  listTradingNextActions,
} from '../../lib/api/endpoints';
import type { PositionIndicatorHistoryDto } from '../../lib/api/endpoints';
import { marketHoursRefetchInterval } from '../../lib/queryRefresh';

export type PositionIndicatorHistoryState =
  | PositionIndicatorHistoryDto
  | { status: 'LOADING' }
  | { status: 'ERROR' };

export const positionKeys = {
  all: ['positions'] as const,
  list: (accountId: string | null) => [...positionKeys.all, 'list', accountId] as const,
  detail: (accountId: string | null, symbol: string | null) =>
    [...positionKeys.all, 'detail', accountId, symbol] as const,
  lifecycle: (accountId: string | null, symbol: string | null) =>
    [...positionKeys.all, 'lifecycle', accountId, symbol] as const,
  replay: (
    accountId: string | null,
    symbol: string | null,
    scope: 'current' | 'full' | undefined,
  ) => [...positionKeys.all, 'replay', accountId, symbol, scope ?? 'default'] as const,
  priceHistory: (accountId: string | null, symbol: string) =>
    [...positionKeys.all, 'price-history', accountId, symbol] as const,
  priceHistories: (accountId: string | null, symbols: readonly string[]) =>
    [...positionKeys.all, 'price-histories', accountId, ...symbols] as const,
  tradingNextActions: (accountId: string | null) =>
    [...positionKeys.all, 'trading-next-actions', accountId] as const,
  tradingPortfolioHeat: (accountId: string | null) =>
    [...positionKeys.all, 'trading-portfolio-heat', accountId] as const,
};

export function usePositions(accountId: string | null) {
  return useQuery({
    queryKey: positionKeys.list(accountId),
    queryFn: () => listPositions({ accountId: accountId ?? '' }),
    enabled: Boolean(accountId),
    refetchInterval: marketHoursRefetchInterval,
  });
}

export function useTradingNextActions(accountId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: positionKeys.tradingNextActions(accountId),
    queryFn: () => listTradingNextActions(accountId ?? ''),
    enabled: Boolean(accountId && enabled),
  });
}

export function useTradingPortfolioHeat(accountId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: positionKeys.tradingPortfolioHeat(accountId),
    queryFn: () => getTradingPortfolioHeat(accountId ?? ''),
    enabled: Boolean(accountId && enabled),
  });
}

export function usePositionDetail(accountId: string | null, symbol: string | null) {
  return useQuery({
    queryKey: positionKeys.detail(accountId, symbol),
    queryFn: () => getPosition({ accountId: accountId ?? '', symbol: symbol ?? '' }),
    enabled: Boolean(accountId && symbol),
  });
}

export function usePositionLifecycle(accountId: string | null, symbol: string | null) {
  return useQuery({
    queryKey: positionKeys.lifecycle(accountId, symbol),
    queryFn: () => getPositionLifecycle({ accountId: accountId ?? '', symbol: symbol ?? '' }),
    enabled: Boolean(accountId && symbol),
  });
}

export function usePositionReplay(
  accountId: string | null,
  symbol: string | null,
  scope?: 'current' | 'full',
) {
  return useQuery({
    queryKey: positionKeys.replay(accountId, symbol, scope),
    queryFn: () =>
      getPositionReplay({
        accountId: accountId ?? '',
        symbol: symbol ?? '',
        ...(scope ? { scope } : {}),
      }),
    enabled: Boolean(accountId && symbol),
  });
}

export function usePositionPriceHistories(
  accountId: string | null,
  symbols: readonly string[],
): Readonly<Record<string, PositionIndicatorHistoryState>> {
  const uniqueSymbols = [...new Set(symbols)];
  const query = useQuery({
    queryKey: positionKeys.priceHistories(accountId, uniqueSymbols),
    queryFn: () =>
      getPositionPriceHistories({ accountId: accountId ?? '', symbols: uniqueSymbols }),
    enabled: Boolean(accountId && uniqueSymbols.length > 0),
  });

  return Object.fromEntries(
    uniqueSymbols.map((symbol): [string, PositionIndicatorHistoryState] => {
      if (query.isPending) return [symbol, { status: 'LOADING' }];
      if (query.isError || !query.data?.[symbol]) return [symbol, { status: 'ERROR' }];
      return [symbol, query.data[symbol]];
    }),
  );
}
